// src/pages/Checkout.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { isIOSApp } from "../utils/platform";

import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");
const HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const STORAGE_CAPS = {
  free: "250 MB",
  monthly_essential: "5 GB",
  monthly_pro: "25 GB",
  monthly_elite: "100 GB",
};

const PLAN_CATALOG = {
  monthly_essential: {
    kind: "monthly",
    label: "Monthly Essential",
    shortLabel: "Essential",
    eyebrow: "Monthly plan",
    icon: "✦",
    amount: 6999,
    priceSuffix: "/month",
    accent: "blue",
    description: "Consistent legal tools for everyday needs.",
    included: [
      "Unlimited Legal Chat",
      "15 Doc Writer documents / month",
      "2 Contract Insight reports / month, up to 25 pages each",
      {
        title: "Document Intelligence",
        details: [
          "Up to 5 pages — Included",
          "6–100 pages — 4 analyses / month",
        ],
      },
      "Case Analyzer available as add-on",
      "10 minutes Vision Assist / month",
      `File Vault: ${STORAGE_CAPS.monthly_essential}`,
    ],
  },
  monthly_pro: {
    kind: "monthly",
    label: "Monthly Pro",
    shortLabel: "Pro",
    eyebrow: "Most popular",
    icon: "♕",
    amount: 12999,
    priceSuffix: "/month",
    accent: "blue",
    description: "For regular users and busy professional workflows.",
    included: [
      "Unlimited Legal Chat",
      "40 Doc Writer documents / month",
      "4 Contract Insight reports / month, up to 25 pages each",
      {
        title: "Document Intelligence",
        details: [
          "Up to 5 pages — Included",
          "6–100 pages — 8 analyses / month",
        ],
      },
      "Case Analyzer available as add-on",
      "25 minutes Vision Assist / month",
      `File Vault: ${STORAGE_CAPS.monthly_pro}`,
    ],
  },
  monthly_elite: {
    kind: "monthly",
    label: "Monthly Elite",
    shortLabel: "Elite",
    eyebrow: "Best value",
    icon: "◇",
    amount: 22999,
    priceSuffix: "/month",
    accent: "green",
    description: "Premium coverage for power users and firms.",
    included: [
      "Unlimited Legal Chat",
      "Unlimited Doc Writer",
      "6 Contract Insight reports / month, up to 25 pages each",
      {
        title: "Document Intelligence",
        details: [
          "Up to 5 pages — Included",
          "6–100 pages — 12 analyses / month",
        ],
      },
      "1 Case Analyzer report / month, up to 250 pages",
      "45 minutes Vision Assist / month",
      `File Vault: ${STORAGE_CAPS.monthly_elite}`,
      "Smart Alert included while plan is active",
    ],
  },
};

const ADDON_CATALOG = {
  case_analyzer: {
    kind: "addon",
    label: "Case Analyzer",
    eyebrow: "Case package report",
    icon: "%",
    accent: "green",
    unitLabel: "report",
    description: "Multi-document case package review with probability profile, evidence map, and saved report.",
    options: {
      250: { amount: 4999, label: "250 pages", title: "Case Analyzer — up to 250 pages", note: "Starter case package." },
      500: { amount: 8999, label: "500 pages", title: "Case Analyzer — up to 500 pages", note: "Most common evidence package." },
      750: { amount: 13999, label: "750 pages", title: "Case Analyzer — up to 750 pages", note: "Larger evidence package." },
      1000: { amount: 18999, label: "1,000 pages", title: "Case Analyzer — up to 1,000 pages", note: "Complex matter package." },
      1250: { amount: 23999, label: "1,250 pages", title: "Case Analyzer — up to 1,250 pages", note: "Heavy document set." },
      1500: { amount: 28999, label: "1,500 pages", title: "Case Analyzer — up to 1,500 pages", note: "Maximum package." },
    },
    included: [
      "One locked case package per report",
      "Probability profile and case score",
      "Evidence map and missing information list",
      "Final report saved to File Vault",
    ],
  },
  contract_insight: {
    kind: "addon",
    label: "Contract Insight",
    eyebrow: "Contract review report",
    icon: "▤",
    accent: "blue",
    unitLabel: "report",
    description: "Structured contract analysis with key terms, flags, scoring, and report output.",
    options: {
      25: { amount: 2499, label: "25 pages", title: "Contract Insight — up to 25 pages", note: "Standard agreement." },
      50: { amount: 3999, label: "50 pages", title: "Contract Insight — up to 50 pages", note: "Medium agreement." },
      75: { amount: 5999, label: "75 pages", title: "Contract Insight — up to 75 pages", note: "Professional review." },
      150: { amount: 9999, label: "150 pages", title: "Contract Insight — up to 150 pages", note: "Complex agreement." },
      250: { amount: 14999, label: "250 pages", title: "Contract Insight — up to 250 pages", note: "Large agreement package." },
    },
    included: [
      "Structured AI contract review",
      "Key terms and red flag summary",
      "Risk-focused report output",
      "File Vault report storage",
    ],
  },
  doc_writer: {
    kind: "addon",
    label: "Doc Writer",
    eyebrow: "Document drafting",
    icon: "✍",
    accent: "blue",
    unitLabel: "document",
    description: "Draft or refine legal-style documents with guided AI support.",
    options: {
      1: { amount: 499, label: "1 Document", title: "Doc Writer — 1 Document", note: "One drafting or refinement credit." },
      5: { amount: 1799, label: "5 Documents", title: "Doc Writer — 5 Documents", note: "Lower per-document cost." },
      15: { amount: 3999, label: "15 Documents", title: "Doc Writer — 15 Documents", note: "Best value for frequent drafting." },
    },
    included: [
      "Guided document drafting",
      "Editable generated text",
      "Reusable document workflow",
      "Good for letters, forms, and legal-style drafts",
    ],
  },
  smart_alert: {
    kind: "addon",
    label: "Smart Alert",
    eyebrow: "Emergency alert package",
    icon: "!",
    accent: "amber",
    unitLabel: "alert",
    description: "Send urgent multi-channel alerts to trusted contacts when timing matters.",
    options: {
      6: { amount: 2999, label: "6 Alerts", title: "Smart Alert — 6 Alerts", note: "Starter alert capacity." },
      12: { amount: 3999, label: "12 Alerts", title: "Smart Alert — 12 Alerts", note: "Double the capacity for ongoing protection." },
      24: { amount: 6999, label: "24 Alerts", title: "Smart Alert — 24 Alerts", note: "Best value for ongoing alert coverage." },
    },
    included: [
      "Multi-channel alert capacity",
      "Trusted contact notification flow",
      "Useful for urgent situations",
      "Standalone add-on package",
    ],
  },
  vision_assist: {
    kind: "addon",
    label: "Vision Assist",
    eyebrow: "Screen-aware help",
    icon: "◉",
    accent: "blue",
    unitLabel: "minute",
    description: "Screen-aware co-pilot support for forms, documents, and step-by-step visual guidance.",
    options: {
      15: { amount: 1499, label: "15 Minutes", title: "Vision Assist — 15 Minutes", note: "Good for a short form or focused task." },
      30: { amount: 2799, label: "30 Minutes", title: "Vision Assist — 30 Minutes", note: "Longer working session for multiple steps." },
      60: { amount: 4999, label: "60 Minutes", title: "Vision Assist — 60 Minutes", note: "Deep-dive support for complex matters." },
    },
    included: [
      "Screen-aware guidance",
      "Form and document assistance",
      "Step-by-step support",
      "Time-based add-on package",
    ],
  },
};

const fmt = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;

function parsePositiveInt(value, fallback) {
  const n = parseInt(value || String(fallback), 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function parsePageTier(value, fallback = 0) {
  const n = parseInt(value || String(fallback), 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function getCheckoutItem(plan, qty, minutes, pageTier) {
  if (!plan) return null;
  if (PLAN_CATALOG[plan]) return PLAN_CATALOG[plan];

  const addOn = ADDON_CATALOG[plan];
  if (!addOn) return null;

  let selectedKey = qty;
  if (plan === "vision_assist") selectedKey = minutes;
  if (plan === "case_analyzer" || plan === "contract_insight") selectedKey = pageTier || qty;

  const option = addOn.options[selectedKey];
  if (!option) return null;

  return {
    ...addOn,
    ...option,
    selectedQty: selectedKey,
    page_tier: plan === "case_analyzer" || plan === "contract_insight" ? selectedKey : null,
    qty: plan !== "vision_assist" && plan !== "case_analyzer" && plan !== "contract_insight" ? selectedKey : undefined,
    minutes: plan === "vision_assist" ? selectedKey : undefined,
    amount: option.amount,
    label: option.title,
    shortLabel: option.label,
    priceSuffix: "",
    product: plan,
    plan,
  };
}

function normalizeCartItemForCheckout(item = {}) {
  const product = String(item.product || item.plan || "").toLowerCase();
  const priceCents = Number(item.price_cents ?? item.priceCents ?? item.amount ?? 0) || 0;

  return {
    id: item.id,
    kind: item.kind || "addon",
    product,
    plan: product,
    label: item.label || item.title || "Cart item",
    shortLabel: item.shortLabel || item.label || item.title || "Cart item",
    title: item.title || item.label || "Cart item",
    description: item.description || item.desc || "",
    eyebrow: item.eyebrow || ADDON_CATALOG[product]?.eyebrow || "Add-on",
    icon: item.icon || ADDON_CATALOG[product]?.icon || "•",
    accent: item.accent || ADDON_CATALOG[product]?.accent || "blue",
    amount: priceCents,
    price_cents: priceCents,
    priceSuffix: "",
    page_tier: item.page_tier || item.pageTier || null,
    pageTier: item.pageTier || item.page_tier || null,
    qty: item.qty || null,
    minutes: item.minutes || null,
    included: item.included || ADDON_CATALOG[product]?.included || [],
  };
}

function buildPaymentItems(items = []) {
  return items.map((item) => ({
    id: item.id || "",
    kind: item.kind || "addon",
    plan: item.plan || item.product || "",
    product: item.product || item.plan || "",
    label: item.label || item.title || "",
    amount: Number(item.amount || item.price_cents || 0) || 0,
    price_cents: Number(item.amount || item.price_cents || 0) || 0,
    qty: item.qty || undefined,
    minutes: item.minutes || undefined,
    page_tier: item.page_tier || item.pageTier || undefined,
  }));
}

const Checkout = () => {
  const { currentUser, userType } = useAuth();
  const { cartItems, cartCount, cartSubtotalCents, removeFromCart, clearCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const plan = (searchParams.get("plan") || "").toLowerCase();
  const qty = parsePositiveInt(searchParams.get("qty"), 1);
  const minutes = parsePositiveInt(searchParams.get("minutes"), 0);
  const pageTier = parsePageTier(searchParams.get("page_tier") || searchParams.get("pages"), 0);

  const legacySelectedItem = useMemo(
    () => getCheckoutItem(plan, qty, minutes, pageTier),
    [plan, qty, minutes, pageTier]
  );

  const isDirectCheckout = Boolean(plan && legacySelectedItem);
  const isCartCheckout = !isDirectCheckout && cartItems.length > 0;

  const checkoutItems = useMemo(() => {
    if (isDirectCheckout && legacySelectedItem) return [legacySelectedItem];
    return cartItems.map(normalizeCartItemForCheckout);
  }, [isDirectCheckout, legacySelectedItem, cartItems]);

  const amount = useMemo(() => {
    if (isDirectCheckout) return legacySelectedItem?.amount || 0;
    return cartSubtotalCents || checkoutItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  }, [isDirectCheckout, legacySelectedItem, cartSubtotalCents, checkoutItems]);

  const selectedItem = checkoutItems[0] || null;
  const isMonthlyCheckout = checkoutItems.length === 1 && selectedItem?.kind === "monthly";
  const summaryTitle = isCartCheckout
    ? `${checkoutItems.length} ${checkoutItems.length === 1 ? "item" : "items"}`
    : selectedItem?.label || "";

  const [clientSecret, setClientSecret] = useState("");
  const [creatingPI, setCreatingPI] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [piRefreshNonce, setPiRefreshNonce] = useState(0);
  const [effectivePromoCode, setEffectivePromoCode] = useState("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const [promoInput, setPromoInput] = useState("");
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [promoSuccess, setPromoSuccess] = useState("");
  const [promoPreview, setPromoPreview] = useState(null);
  const [appliedPromoCode, setAppliedPromoCode] = useState("");
  const [appliedPromoGroup, setAppliedPromoGroup] = useState("");
  const [appliedDiscountCents, setAppliedDiscountCents] = useState(0);
  const [piError, setPiError] = useState("");

  const latestPiRequestRef = useRef(0);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)");
    const apply = () => setIsSmallScreen(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  useEffect(() => {
    if (currentUser === null) navigate("/login");
  }, [currentUser, navigate]);

  useEffect(() => {
    if (currentUser?.email) setEmail(currentUser.email);
    if (currentUser?.displayName) setName(currentUser.displayName);
  }, [currentUser]);

  const rawPromoCode = useMemo(() => (promoInput || "").trim().toUpperCase(), [promoInput]);

  const computePromoPreview = (result) => {
    const normalizedPlan = (plan || "").toLowerCase();
    const preview = {
      code: (result?.promo_code || "").toUpperCase(),
      group: result?.group || "",
      type: result?.promo_type || "",
      discountCents: 0,
      message: "",
      userFacingTitle: "",
    };

    if (preview.group !== "influencer") {
      preview.message = "This code is not valid for checkout.";
      return preview;
    }

    if (!isDirectCheckout && checkoutItems.length > 1) {
      preview.userFacingTitle = "Promo applied";
      preview.message = "This promo code is valid for your cart. The final discount will be confirmed before payment.";
      return preview;
    }

    if (normalizedPlan === "smart_alert") {
      const smartAlertPolicy = result?.policy?.customer_benefits?.addons?.smart_alert || {};
      const targetKey = qty === 6 ? "smart_alert_6" : qty === 12 ? "smart_alert_12" : qty === 24 ? "smart_alert_24" : "";
      const canDiscount = smartAlertPolicy?.enabled === true && !!targetKey && !!smartAlertPolicy?.plans?.[targetKey];

      if (canDiscount) {
        const usd = Number(smartAlertPolicy?.discount_amount_usd || 0);
        const cents = Number.isFinite(usd) && usd > 0 ? Math.round(usd * 100) : 0;
        preview.discountCents = Math.max(0, Math.min(cents, amount));
        if (preview.discountCents > 0) {
          preview.userFacingTitle = "Promo applied";
          preview.message = `${fmt(preview.discountCents)} off will be applied to this Smart Alert purchase.`;
          return preview;
        }
      }
    }

    if (["monthly_essential", "monthly_pro", "monthly_elite"].includes(normalizedPlan)) {
      preview.userFacingTitle = "Promo applied";
      preview.message = "Extra plan benefits will apply after purchase.";
      return preview;
    }

    preview.userFacingTitle = "Promo applied";
    preview.message = "This promo code is valid for your account.";
    return preview;
  };

  const finalDisplayAmount = useMemo(
    () => Math.max(0, amount - appliedDiscountCents),
    [amount, appliedDiscountCents]
  );

  const clearPromoState = () => {
    setPromoError("");
    setPromoSuccess("");
    setPromoPreview(null);
    setAppliedPromoCode("");
    setAppliedPromoGroup("");
    setAppliedDiscountCents(0);
    setPiError("");
  };

  const handleValidatePromo = async () => {
    if (!currentUser?.email) return;
    const code = rawPromoCode;

    if (!code) {
      setPromoError("Enter a promo code first.");
      setPromoSuccess("");
      setPromoPreview(null);
      setAppliedPromoCode("");
      setAppliedPromoGroup("");
      setAppliedDiscountCents(0);
      setPiError("");
      setEffectivePromoCode("");
      setPiRefreshNonce((n) => n + 1);
      return;
    }

    setValidatingPromo(true);
    setPromoError("");
    setPromoSuccess("");
    setPiError("");

    try {
      const payload = {
        promo_code: code,
        email: currentUser.email || "",
        user_type: userType || "unknown",
        first_name: "",
        last_name: "",
        checkout_mode: isCartCheckout ? "cart" : "single",
        items: buildPaymentItems(checkoutItems),
      };

      const res = await fetch(`${HOST}/validate-promo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.success) {
        setPromoError(data?.message || `Promo validation failed (HTTP ${res.status}).`);
        setPromoSuccess("");
        setPromoPreview(null);
        setAppliedPromoCode("");
        setAppliedPromoGroup("");
        setAppliedDiscountCents(0);
        setEffectivePromoCode(code);
        setPiRefreshNonce((n) => n + 1);
        return;
      }

      if ((data?.group || "").toLowerCase() === "private") {
        setPromoError("This private invitation code is not valid for checkout.");
        setPromoSuccess("");
        setPromoPreview(null);
        setAppliedPromoCode("");
        setAppliedPromoGroup("");
        setAppliedDiscountCents(0);
        setEffectivePromoCode(code);
        setPiRefreshNonce((n) => n + 1);
        return;
      }

      const preview = computePromoPreview(data);
      setPromoPreview(preview);
      setAppliedPromoCode((data?.promo_code || code).toUpperCase());
      setAppliedPromoGroup((data?.group || "").toLowerCase());
      setAppliedDiscountCents(preview.discountCents || 0);
      setPromoSuccess(preview.message || "Promo code validated.");
      setPromoError("");
      setEffectivePromoCode(code);
      setPiRefreshNonce((n) => n + 1);
    } catch (err) {
      console.error("[Checkout] validate promo error", err);
      setPromoError("Unexpected error validating promo code.");
      setPromoSuccess("");
      setPromoPreview(null);
      setAppliedPromoCode("");
      setAppliedPromoGroup("");
      setAppliedDiscountCents(0);
      setEffectivePromoCode(code);
      setPiRefreshNonce((n) => n + 1);
    } finally {
      setValidatingPromo(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoInput("");
    clearPromoState();
    setEffectivePromoCode("");
    setPiRefreshNonce((n) => n + 1);
  };

  useEffect(() => {
    if (!amount) return;
    if (!currentUser) return;
    if (isIOSApp()) return;

    const timeout = setTimeout(() => {
      setEffectivePromoCode(rawPromoCode);
      setPiRefreshNonce((n) => n + 1);
    }, 700);

    return () => clearTimeout(timeout);
  }, [rawPromoCode, currentUser, amount]);

  useEffect(() => {
    const createPI = async () => {
      if (!amount) return;
      if (!checkoutItems.length) return;
      if (!currentUser) return;
      if (isIOSApp()) return;

      const requestId = latestPiRequestRef.current + 1;
      latestPiRequestRef.current = requestId;

      setCreatingPI(true);
      setPiError("");

      try {
        const idToken = await currentUser.getIdToken();
        const paymentItems = buildPaymentItems(checkoutItems);
        const primary = paymentItems[0] || {};

        // Backend contract:
        // - cart checkout sends `items`
        // - direct monthly/single checkout sends `plan` and no `items`
        // Stripe treats any non-empty `items` array as cart checkout.
        const payload = isCartCheckout
          ? {
              email: currentUser.email || null,
              user_type: userType || "unknown",
              checkout_mode: "cart",
              items: paymentItems,
              amount,
              promo_code: effectivePromoCode || undefined,
            }
          : {
              email: currentUser.email || null,
              user_type: userType || "unknown",
              checkout_mode: "single",
              plan: primary.plan || plan,
              qty,
              minutes: (primary.plan || plan) === "vision_assist" ? minutes : undefined,
              page_tier: primary.page_tier || undefined,
              amount,
              promo_code: effectivePromoCode || undefined,
            };

        const res = await fetch(`${HOST}/create-payment-intent`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify(payload),
        });

        const data = await res.json().catch(() => ({}));
        if (requestId !== latestPiRequestRef.current) return;

        if (res.ok && data?.clientSecret) {
          setClientSecret(data.clientSecret);

          const backendPromoDiscount = Number(data?.promo?.promo_discount_cents || 0);
          setAppliedDiscountCents(Number.isFinite(backendPromoDiscount) && backendPromoDiscount > 0 ? backendPromoDiscount : 0);

          if (data?.promo?.promo_code) setAppliedPromoCode(String(data.promo.promo_code).toUpperCase());
          else if (!effectivePromoCode) setAppliedPromoCode("");

          if (data?.promo?.promo_group) setAppliedPromoGroup(String(data.promo.promo_group).toLowerCase());
          else if (!effectivePromoCode) setAppliedPromoGroup("");

          setPiError("");
        } else {
          setClientSecret("");
          setAppliedDiscountCents(0);
          setAppliedPromoCode("");
          setAppliedPromoGroup("");
          setPiError(data?.error || `HTTP ${res.status}`);
        }
      } catch (err) {
        if (requestId !== latestPiRequestRef.current) return;
        console.error("[Checkout] createPI error", err);
        setClientSecret("");
        setAppliedDiscountCents(0);
        setAppliedPromoCode("");
        setAppliedPromoGroup("");
        setPiError("Unexpected error creating PaymentIntent.");
      } finally {
        if (requestId === latestPiRequestRef.current) setCreatingPI(false);
      }
    };

    createPI();
  }, [
    currentUser,
    userType,
    plan,
    qty,
    minutes,
    amount,
    checkoutItems,
    effectivePromoCode,
    piRefreshNonce,
    isCartCheckout,
  ]);

  if (isIOSApp()) {
    return (
      <div style={styles.pageShell}>
        <section style={styles.centerCard}>
          <div style={styles.centerIcon}>i</div>
          <h1 style={styles.centerTitle}>Checkout Unavailable</h1>
          <p style={styles.centerText}>
            Purchasing plans is not supported inside the iOS app. Please sign in using your browser at{" "}
            <a href="https://www.asklawly.com" target="_blank" rel="noopener noreferrer" style={styles.link}>www.asklawly.com</a>{" "}
            to complete your checkout.
          </p>
        </section>
      </div>
    );
  }

  const appearance = { theme: "stripe" };

  const promoSummaryCard = promoPreview || promoError || promoSuccess ? (
    <div style={{ ...styles.promoNotice, ...(promoError ? styles.promoNoticeError : null) }}>
      {promoError ? (
        <div style={styles.promoError}>{promoError}</div>
      ) : (
        <>
          <div style={styles.promoTitle}>{promoPreview?.userFacingTitle || "Promo applied"}</div>
          <div style={styles.promoText}>{promoSuccess}</div>
          {!!appliedPromoCode && <div style={styles.promoCode}>Code: {appliedPromoCode}</div>}
        </>
      )}
    </div>
  ) : null;

  const unsupported = !checkoutItems.length || !amount;

  return (
    <div style={styles.pageShell}>
      {unsupported ? (
        <section style={styles.centerCard}>
          <div style={styles.centerIcon}>!</div>
          <h1 style={styles.centerTitle}>Checkout is empty</h1>
          <p style={styles.centerText}>
            Your checkout does not have a supported plan or cart item. Please go back to Billing and select an option again.
          </p>
          <button type="button" style={styles.primaryButton} onClick={() => navigate("/billing")}>Back to Billing</button>
        </section>
      ) : (
        <>
          <section style={{ ...styles.hero, ...(isSmallScreen ? styles.heroMobile : null) }}>
            <div>
              <h1 style={{ ...styles.h1, ...(isSmallScreen ? styles.h1Mobile : null) }}>Complete your purchase</h1>
              <p style={styles.heroText}>
                Review your {isCartCheckout ? "cart items" : "selection"}, apply a promo code if available, and complete payment securely.
              </p>
            </div>
            <div style={styles.heroMiniCard}>
              <div style={styles.heroMiniLabel}>{isCartCheckout ? "Cart total" : "Selected"}</div>
              <div style={styles.heroMiniTitle}>{summaryTitle}</div>
              <div style={styles.heroMiniPrice}>{fmt(finalDisplayAmount)}{!isCartCheckout && selectedItem?.priceSuffix ? selectedItem.priceSuffix : ""}</div>
            </div>
          </section>

          <div style={{ ...styles.checkoutGrid, ...(isSmallScreen ? styles.checkoutGridMobile : null) }}>
            <main style={styles.leftColumn}>
              <section style={styles.card}>
                <div style={styles.sectionTop}>
                  <div>
                    <div style={styles.sectionEyebrow}>{isCartCheckout ? "Cart review" : selectedItem.eyebrow}</div>
                    <h2 style={styles.sectionTitle}>{isCartCheckout ? "Review selected items" : selectedItem.label}</h2>
                    <p style={styles.sectionSub}>
                      {isCartCheckout ? "You can remove items here or go back to Billing to add more." : selectedItem.description}
                    </p>
                  </div>
                  <div style={{ ...styles.productIcon, ...(selectedItem?.accent === "green" ? styles.productIconGreen : selectedItem?.accent === "amber" ? styles.productIconAmber : null) }}>
                    {isCartCheckout ? cartCount : selectedItem.icon}
                  </div>
                </div>

                {isCartCheckout ? (
                  <div style={styles.cartItemStack}>
                    {checkoutItems.map((item) => (
                      <div key={item.id || item.label} style={styles.cartItemRow}>
                        <div style={styles.cartItemMain}>
                          <div style={styles.cartItemTitle}>{item.label}</div>
                          <div style={styles.cartItemMeta}>
                            {item.page_tier ? `Up to ${Number(item.page_tier).toLocaleString()} pages` : item.minutes ? `${item.minutes} minutes` : item.qty ? `${item.qty} ${Number(item.qty) === 1 ? "credit" : "credits"}` : item.kind || "Add-on"}
                          </div>
                        </div>
                        <div style={styles.cartItemRight}>
                          <strong>{fmt(item.amount)}</strong>
                          <button type="button" style={styles.removeItemBtn} onClick={() => removeFromCart(item.id)}>
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                    <button type="button" style={styles.secondaryButton} onClick={() => navigate("/billing")}>
                      Add more items
                    </button>
                  </div>
                ) : (
                  <div style={styles.includedGrid}>
                    {(selectedItem.included || []).map((item, idx) => {
                      const isStructuredFeature =
                        item && typeof item === "object" && !React.isValidElement(item);

                      return (
                        <div key={idx} style={styles.includedItem}>
                          <span style={styles.checkDot}>✓</span>
                          {isStructuredFeature ? (
                            <div style={styles.includedFeatureBlock}>
                              <div style={styles.includedFeatureTitle}>{item.title}</div>
                              {(item.details || []).map((detail, detailIndex) => (
                                <div
                                  key={`${idx}-${detailIndex}`}
                                  style={styles.includedFeatureDetail}
                                >
                                  {detail}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span>{item}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              <section style={styles.card}>
                <div style={styles.sectionTopCompact}>
                  <div>
                    <div style={styles.sectionEyebrow}>Customer information</div>
                    <h2 style={styles.sectionTitle}>Contact details</h2>
                  </div>
                </div>

                <div style={{ ...styles.fieldGrid, ...(isSmallScreen ? styles.fieldGridMobile : null) }}>
                  <label style={styles.field}>
                    <span style={styles.label}>Full Name</span>
                    <input value={name} onChange={(e) => setName(e.target.value)} type="text" placeholder="John Doe" style={styles.input} />
                  </label>
                  <label style={styles.field}>
                    <span style={styles.label}>Email Address</span>
                    <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@example.com" style={styles.input} />
                  </label>
                </div>
              </section>

              <section style={styles.card}>
                <div style={styles.sectionTopCompact}>
                  <div>
                    <div style={styles.sectionEyebrow}>Promo code</div>
                    <h2 style={styles.sectionTitle}>Apply discount</h2>
                  </div>
                </div>

                <div style={{ ...styles.promoRow, ...(isSmallScreen ? styles.promoRowMobile : null) }}>
                  <input
                    type="text"
                    placeholder="Enter promo code"
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                    style={styles.input}
                  />
                  {appliedPromoCode ? (
                    <button type="button" onClick={handleRemovePromo} style={styles.secondaryButton}>Remove</button>
                  ) : (
                    <button type="button" onClick={handleValidatePromo} disabled={validatingPromo} style={{ ...styles.secondaryButton, ...(validatingPromo ? styles.disabledButton : null) }}>
                      {validatingPromo ? "Checking…" : "Apply"}
                    </button>
                  )}
                </div>
                {promoSummaryCard}
              </section>

              <section style={styles.card}>
                <div style={styles.sectionTopCompact}>
                  <div>
                    <div style={styles.sectionEyebrow}>Payment</div>
                    <h2 style={styles.sectionTitle}>Payment method</h2>
                    <p style={styles.sectionSub}>Your card details are handled securely by Stripe.</p>
                  </div>
                </div>

                {creatingPI && <div style={styles.notice}>Preparing secure payment form…</div>}
                {!!piError && <div style={{ ...styles.notice, ...styles.errorNotice }}>{piError}</div>}

                {clientSecret ? (
                  <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
                    <StripeCheckoutForm
                      checkoutMode={isCartCheckout ? "cart" : "single"}
                      plan={isDirectCheckout ? plan : ""}
                      qty={qty}
                      minutes={minutes}
                      items={buildPaymentItems(checkoutItems)}
                      amount={finalDisplayAmount}
                      promoCode={appliedPromoCode}
                      name={name}
                      email={email}
                      clearCart={isCartCheckout ? clearCart : undefined}
                      onSuccess={() => navigate("/billing")}
                    />
                  </Elements>
                ) : !creatingPI && !piError ? (
                  <div style={styles.notice}>Payment form is not ready. Please refresh or return to Billing and try again.</div>
                ) : null}
              </section>
            </main>

            <aside style={styles.rightColumn}>
              <section style={styles.summaryCard}>
                <div style={styles.summaryHeader}>
                  <div>
                    <div style={styles.summaryEyebrow}>Order summary</div>
                    <div style={styles.summaryTitle}>{summaryTitle}</div>
                  </div>
                  <div style={styles.summaryBadge}>{isMonthlyCheckout ? "Monthly" : isCartCheckout ? "Cart" : "Add-on"}</div>
                </div>

                <div style={styles.lineItems}>
                  {checkoutItems.map((item) => (
                    <div key={item.id || item.label} style={styles.lineItem}>
                      <span>{item.label}</span>
                      <strong>{fmt(item.amount)}{item.priceSuffix || ""}</strong>
                    </div>
                  ))}
                  {appliedDiscountCents > 0 && (
                    <div style={styles.lineItemGreen}>
                      <span>Promo discount</span>
                      <strong>-{fmt(appliedDiscountCents)}</strong>
                    </div>
                  )}
                </div>

                <div style={styles.totalRow}>
                  <span>Total due today</span>
                  <strong>{fmt(finalDisplayAmount)}{!isCartCheckout && selectedItem?.priceSuffix ? selectedItem.priceSuffix : ""}</strong>
                </div>

                {isMonthlyCheckout ? (
                  <p style={styles.summaryFinePrint}>Monthly plans renew automatically unless canceled. You can manage your plan from Billing.</p>
                ) : (
                  <p style={styles.summaryFinePrint}>Add-ons are credited to your account after payment confirmation.</p>
                )}
              </section>

              <section style={styles.trustCard}>
                <div style={styles.trustTitle}>What happens next</div>
                <StepItem number="1" title="Payment confirms" text="Stripe securely processes your payment." />
                <StepItem number="2" title="Credits apply" text="Your plan or add-on allowance is added to your account." />
                <StepItem number="3" title="Return to Billing" text="You can view your updated usage from the Billing page." />
              </section>
            </aside>
          </div>
        </>
      )}
    </div>
  );
};

const StripeCheckoutForm = ({ checkoutMode, plan, qty, minutes, items, amount, promoCode, name, email, clearCart, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const disabled = !stripe || !elements || submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setError("");

    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success?checkout_mode=${encodeURIComponent(checkoutMode || "single")}&plan=${encodeURIComponent(plan || "")}&qty=${encodeURIComponent(qty || "")}&minutes=${encodeURIComponent(minutes || "")}`,
          payment_method_data: {
            billing_details: {
              name: name || undefined,
              email: email || undefined,
            },
          },
        },
        redirect: "if_required",
      });

      if (result?.error) {
        setError(result.error.message || "Payment failed. Please try again.");
        setSubmitting(false);
        return;
      }

      if (typeof clearCart === "function") clearCart();
      onSuccess?.();
    } catch (err) {
      console.error("[Checkout] payment submit error", err);
      setError(err?.message || "Unexpected error confirming payment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={styles.paymentForm}>
      <div style={styles.paymentElementBox}>
        <PaymentElement />
      </div>
      {!!error && <div style={{ ...styles.notice, ...styles.errorNotice }}>{error}</div>}
      {!!promoCode && <div style={styles.smallSuccess}>Promo code {promoCode} is attached to this checkout.</div>}
      <button type="submit" disabled={disabled} style={{ ...styles.payButton, ...(disabled ? styles.disabledButton : null) }}>
        {submitting ? "Processing…" : `Pay ${fmt(amount)}`}
      </button>
    </form>
  );
};

const StepItem = ({ number, title, text }) => (
  <div style={styles.stepItem}>
    <div style={styles.stepNumber}>{number}</div>
    <div>
      <div style={styles.stepTitle}>{title}</div>
      <div style={styles.stepText}>{text}</div>
    </div>
  </div>
);

const styles = {
  pageShell: {
    maxWidth: 1120,
    margin: "0 auto",
    padding: "18px 16px 42px",
    boxSizing: "border-box",
    background: "#F5F7FA",
    fontFamily: "sans-serif",
  },
  hero: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 280px",
    gap: 16,
    alignItems: "stretch",
    marginBottom: 16,
  },
  heroMobile: {
    gridTemplateColumns: "1fr",
    textAlign: "center",
  },
  h1: {
    margin: 0,
    color: "#0F172A",
    fontSize: 36,
    lineHeight: 1.06,
    fontWeight: 950,
    letterSpacing: "-1px",
  },
  h1Mobile: {
    fontSize: 30,
    textAlign: "center",
  },
  heroText: {
    margin: "9px 0 0",
    color: "#475569",
    fontSize: 15,
    lineHeight: 1.55,
    fontWeight: 650,
    maxWidth: 700,
  },
  heroMiniCard: {
    borderRadius: 20,
    background: "linear-gradient(135deg, #FFFFFF 0%, #EEF2FF 100%)",
    border: "1px solid rgba(37,99,235,0.14)",
    boxShadow: "0 14px 34px rgba(15,23,42,0.08)",
    padding: 16,
  },
  heroMiniLabel: {
    color: "#64748B",
    fontSize: 12,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  heroMiniTitle: {
    color: "#0F172A",
    fontSize: 17,
    fontWeight: 950,
    marginTop: 7,
  },
  heroMiniPrice: {
    color: "#2563EB",
    fontSize: 28,
    fontWeight: 950,
    marginTop: 10,
    letterSpacing: "-0.7px",
  },
  checkoutGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 340px",
    gap: 16,
    alignItems: "start",
  },
  checkoutGridMobile: {
    gridTemplateColumns: "1fr",
  },
  leftColumn: {
    display: "grid",
    gap: 14,
    minWidth: 0,
  },
  rightColumn: {
    display: "grid",
    gap: 14,
    minWidth: 0,
  },
  card: {
    borderRadius: 22,
    background: "#FFFFFF",
    border: "1px solid rgba(15,23,42,0.08)",
    boxShadow: "0 12px 30px rgba(15,23,42,0.06)",
    padding: 18,
    boxSizing: "border-box",
  },
  sectionTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 14,
    marginBottom: 14,
  },
  sectionTopCompact: {
    marginBottom: 14,
  },
  sectionEyebrow: {
    color: "#64748B",
    fontSize: 12,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  sectionTitle: {
    margin: "5px 0 0",
    color: "#0F172A",
    fontSize: 22,
    fontWeight: 950,
    letterSpacing: "-0.45px",
  },
  sectionSub: {
    margin: "7px 0 0",
    color: "#64748B",
    fontSize: 13.5,
    lineHeight: 1.5,
    fontWeight: 650,
  },
  productIcon: {
    width: 52,
    height: 52,
    minWidth: 52,
    borderRadius: 17,
    background: "rgba(37,99,235,0.10)",
    color: "#2563EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 25,
    fontWeight: 950,
  },
  productIconGreen: {
    background: "rgba(5,150,105,0.10)",
    color: "#047857",
  },
  productIconAmber: {
    background: "rgba(245,158,11,0.12)",
    color: "#B45309",
  },
  includedGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
  },
  includedItem: {
    display: "grid",
    gridTemplateColumns: "22px minmax(0, 1fr)",
    gap: 8,
    alignItems: "start",
    color: "#334155",
    fontSize: 13.5,
    lineHeight: 1.4,
    fontWeight: 750,
  },
  includedFeatureBlock: {
    minWidth: 0,
  },
  includedFeatureTitle: {
    color: "#334155",
    fontSize: 13.5,
    lineHeight: 1.35,
    fontWeight: 900,
  },
  includedFeatureDetail: {
    color: "#64748B",
    fontSize: 12.5,
    lineHeight: 1.4,
    fontWeight: 750,
    marginTop: 2,
  },
  checkDot: {
    width: 20,
    height: 20,
    borderRadius: 999,
    background: "rgba(5,150,105,0.10)",
    color: "#047857",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 950,
  },
  cartItemStack: {
    display: "grid",
    gap: 10,
  },
  cartItemRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#F8FAFC",
    borderRadius: 16,
    padding: 12,
  },
  cartItemMain: {
    minWidth: 0,
  },
  cartItemTitle: {
    color: "#0F172A",
    fontWeight: 950,
    fontSize: 14,
    lineHeight: 1.35,
  },
  cartItemMeta: {
    marginTop: 4,
    color: "#64748B",
    fontSize: 12.5,
    fontWeight: 750,
  },
  cartItemRight: {
    display: "grid",
    justifyItems: "end",
    gap: 6,
    whiteSpace: "nowrap",
    color: "#0F172A",
  },
  removeItemBtn: {
    border: "none",
    background: "transparent",
    color: "#B91C1C",
    cursor: "pointer",
    fontWeight: 900,
    padding: 0,
  },
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },
  fieldGridMobile: {
    gridTemplateColumns: "1fr",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 7,
    minWidth: 0,
  },
  label: {
    color: "#334155",
    fontSize: 13,
    fontWeight: 900,
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px 13px",
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,0.15)",
    background: "#FFFFFF",
    color: "#0F172A",
    outline: "none",
    fontSize: 14,
    fontWeight: 700,
  },
  promoRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 10,
    alignItems: "center",
  },
  promoRowMobile: {
    gridTemplateColumns: "1fr",
  },
  secondaryButton: {
    appearance: "none",
    border: "1px solid rgba(30,42,120,0.20)",
    borderRadius: 12,
    background: "#FFFFFF",
    color: "#1E2A78",
    padding: "12px 14px",
    fontSize: 13.5,
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  primaryButton: {
    appearance: "none",
    border: "1px solid rgba(37,99,235,0.22)",
    borderRadius: 12,
    background: "#2563EB",
    color: "#FFFFFF",
    padding: "12px 15px",
    fontSize: 13.5,
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 10px 22px rgba(37,99,235,0.18)",
  },
  disabledButton: {
    opacity: 0.62,
    cursor: "not-allowed",
    boxShadow: "none",
  },
  promoNotice: {
    marginTop: 11,
    borderRadius: 14,
    border: "1px solid rgba(30,42,120,0.18)",
    background: "rgba(30,42,120,0.05)",
    padding: "12px 14px",
  },
  promoNoticeError: {
    border: "1px solid rgba(185,28,28,0.22)",
    background: "rgba(254,242,242,1)",
  },
  promoTitle: {
    color: "#0F172A",
    fontWeight: 950,
    fontSize: 13.5,
  },
  promoText: {
    color: "#334155",
    marginTop: 4,
    lineHeight: 1.5,
    fontSize: 13.5,
    fontWeight: 700,
  },
  promoCode: {
    color: "#64748B",
    marginTop: 6,
    fontSize: 12.5,
    fontWeight: 800,
  },
  promoError: {
    color: "#B91C1C",
    fontWeight: 850,
    fontSize: 13.5,
  },
  notice: {
    borderRadius: 14,
    background: "#F8FAFC",
    border: "1px solid rgba(15,23,42,0.08)",
    padding: 12,
    color: "#475569",
    fontSize: 13.5,
    lineHeight: 1.5,
    fontWeight: 750,
    marginBottom: 12,
  },
  errorNotice: {
    color: "#B91C1C",
    background: "rgba(254,242,242,1)",
    borderColor: "rgba(185,28,28,0.20)",
  },
  smallSuccess: {
    marginTop: 10,
    color: "#047857",
    fontSize: 12.5,
    fontWeight: 850,
  },
  paymentForm: {
    display: "grid",
    gap: 12,
  },
  paymentElementBox: {
    padding: 13,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "#FFFFFF",
  },
  payButton: {
    appearance: "none",
    border: "1px solid rgba(37,99,235,0.22)",
    borderRadius: 14,
    background: "#2563EB",
    color: "#FFFFFF",
    padding: "14px 16px",
    fontSize: 15,
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 12px 26px rgba(37,99,235,0.22)",
  },
  summaryCard: {
    borderRadius: 22,
    background: "#FFFFFF",
    border: "1px solid rgba(15,23,42,0.08)",
    boxShadow: "0 12px 30px rgba(15,23,42,0.06)",
    padding: 18,
    position: "sticky",
    top: 18,
  },
  summaryHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 16,
  },
  summaryEyebrow: {
    color: "#64748B",
    fontSize: 12,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  summaryTitle: {
    color: "#0F172A",
    fontSize: 19,
    fontWeight: 950,
    marginTop: 5,
  },
  summaryBadge: {
    borderRadius: 999,
    background: "rgba(37,99,235,0.09)",
    color: "#1E2A78",
    border: "1px solid rgba(37,99,235,0.16)",
    padding: "6px 9px",
    fontSize: 12,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  lineItems: {
    display: "grid",
    gap: 10,
    padding: "14px 0",
    borderTop: "1px solid rgba(15,23,42,0.08)",
    borderBottom: "1px solid rgba(15,23,42,0.08)",
  },
  lineItem: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    color: "#334155",
    fontSize: 13.5,
    lineHeight: 1.4,
    fontWeight: 750,
  },
  lineItemGreen: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    color: "#047857",
    fontSize: 13.5,
    lineHeight: 1.4,
    fontWeight: 850,
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: 12,
    color: "#0F172A",
    fontSize: 16,
    fontWeight: 950,
    paddingTop: 15,
  },
  summaryFinePrint: {
    margin: "12px 0 0",
    color: "#64748B",
    fontSize: 12.5,
    lineHeight: 1.5,
    fontWeight: 700,
  },
  trustCard: {
    borderRadius: 22,
    background: "#FFFFFF",
    border: "1px solid rgba(15,23,42,0.08)",
    boxShadow: "0 12px 30px rgba(15,23,42,0.06)",
    padding: 18,
  },
  trustTitle: {
    color: "#0F172A",
    fontSize: 16,
    fontWeight: 950,
    marginBottom: 12,
  },
  stepItem: {
    display: "grid",
    gridTemplateColumns: "30px minmax(0, 1fr)",
    gap: 10,
    marginBottom: 12,
  },
  stepNumber: {
    width: 26,
    height: 26,
    borderRadius: 999,
    background: "rgba(37,99,235,0.09)",
    color: "#2563EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 950,
  },
  stepTitle: {
    color: "#0F172A",
    fontSize: 13.5,
    fontWeight: 950,
  },
  stepText: {
    color: "#64748B",
    fontSize: 12.5,
    lineHeight: 1.45,
    marginTop: 3,
    fontWeight: 650,
  },
  centerCard: {
    maxWidth: 520,
    margin: "70px auto",
    borderRadius: 24,
    background: "#FFFFFF",
    border: "1px solid rgba(15,23,42,0.08)",
    boxShadow: "0 16px 42px rgba(15,23,42,0.08)",
    padding: 26,
    textAlign: "center",
  },
  centerIcon: {
    width: 46,
    height: 46,
    borderRadius: 16,
    background: "rgba(37,99,235,0.09)",
    color: "#2563EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 12px",
    fontWeight: 950,
    fontSize: 20,
  },
  centerTitle: {
    margin: 0,
    color: "#0F172A",
    fontSize: 24,
    fontWeight: 950,
    letterSpacing: "-0.5px",
  },
  centerText: {
    color: "#475569",
    lineHeight: 1.6,
    fontSize: 15,
    margin: "12px 0 18px",
  },
  link: {
    color: "#1E2A78",
    textDecoration: "underline",
    fontWeight: 900,
  },
};

export default Checkout;
